import { addCalendarDays, dateInTimezone, organizationTimezone } from '@/features/team/timeEntries';
import { dueDateForTerms } from '@/features/invoices/paymentTerms';

/**
 * Org defaults a new estimate starts with (#1309): Settings → My LightMaster →
 * Invoice & estimate notes → Estimate defaults.
 */
export const DEFAULT_ESTIMATE_VALID_DAYS = 30;
export const DEFAULT_PAYMENT_TERMS = 'Net 30';

type OrgDefaults = { default_estimate_valid_days?: number | null; default_payment_terms?: string | null; timezone?: string | null } | null | undefined;

/** The org's "Estimates valid for N days" (1-365), 30 when unset or out of range. */
export function orgEstimateValidDays(org: OrgDefaults): number {
  const days = Number(org?.default_estimate_valid_days);
  return Number.isInteger(days) && days >= 1 && days <= 365 ? days : DEFAULT_ESTIMATE_VALID_DAYS;
}

/** The org's default payment terms; '' means no payment terms. Net 30 when unset. */
export function orgDefaultPaymentTerms(org: OrgDefaults): string {
  const terms = org?.default_payment_terms;
  return typeof terms === 'string' ? terms.trim() : DEFAULT_PAYMENT_TERMS;
}

/**
 * Valid until for a new estimate: today on the business's calendar (its
 * timezone, not the browser's and not UTC) plus the org's day count.
 */
export function newEstimateValidUntil(now: Date, org: OrgDefaults): string {
  return addCalendarDays(dateInTimezone(now, organizationTimezone(org)), orgEstimateValidDays(org));
}

/**
 * Terms and due date for a new invoice built from scratch: the org's default
 * terms, and the due date those terms give an invoice issued today on the
 * business's calendar (Due on receipt = today, Net N = +N, else +30).
 */
export function newInvoiceTermsAndDueDate(now: Date, org: OrgDefaults): { paymentTerms: string; dueDate: string } {
  const paymentTerms = orgDefaultPaymentTerms(org);
  return { paymentTerms, dueDate: dueDateForTerms(paymentTerms, dateInTimezone(now, organizationTimezone(org))) };
}
